import { useDroppable } from "@dnd-kit/core";
import type {
  Timeslot,
  ScheduleAssignment,
  TimeslotPreference,
  Student,
} from "../types";
import "./TimeslotDropZone.css";

interface Props {
  timeslot: Timeslot;
  assignment?: ScheduleAssignment;
  preferences?: TimeslotPreference[];
  students?: Student[];
  onRemove?: (assignment: ScheduleAssignment) => void;
  style?: React.CSSProperties;
}

export function TimeslotDropZone({
  timeslot,
  assignment,
  preferences = [],
  students = [],
  onRemove,
  style,
}: Props) {
  const { setNodeRef, isOver } = useDroppable({
    id: `timeslot-${timeslot.id}`,
    data: { timeslot },
  });

  // Group preferences by type
  const preferredPrefs = preferences.filter((p) => p.type === "preferred");
  const alternativePrefs = preferences.filter((p) => p.type === "alternative");
  const blockHourPrefs = preferences.filter((p) => p.type === "block_hour");

  // Get student name by ID
  const getStudentName = (studentId: number) => {
    return students.find((s) => s.id === studentId)?.name || "Unknown";
  };

  const [sh, sm] = timeslot.start_time.split(":").map(Number);
  const startMin = sh * 60 + sm;
  const end = startMin + timeslot.duration;
  const eh = Math.floor(end / 60);
  const em = end % 60;
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  const displayStart = `${pad(sh)}:${pad(sm)}`;
  const displayEnd = `${pad(eh)}:${pad(em)}`;

  return (
    <div
      ref={setNodeRef}
      className={`timeslot-dropzone ${isOver ? "drag-over" : ""} ${assignment ? "has-assignment" : ""}`}
      style={style}
    >
      <div className="timeslot-label">
        {displayStart} – {displayEnd}
      </div>

      {assignment && (
        <div className="assignment">
          <div className="assignment-student">👤 {assignment.student_name}</div>
          {onRemove && (
            <button
              className="remove-btn"
              onClick={() => onRemove(assignment)}
              title="Remove assignment"
            >
              ×
            </button>
          )}
        </div>
      )}

      {!assignment && preferences.length > 0 && (
        <div className="preferences-list">
          {preferredPrefs.map((pref) => (
            <div key={pref.id} className="preference-badge preferred">
              ⭐ {getStudentName(pref.student_id)}
            </div>
          ))}
          {alternativePrefs.map((pref) => (
            <div key={pref.id} className="preference-badge alternative">
              ⚠️ {getStudentName(pref.student_id)}
            </div>
          ))}
          {blockHourPrefs.map((pref) => (
            <div key={pref.id} className="preference-badge block-hour">
              🚫 {getStudentName(pref.student_id)}
            </div>
          ))}
        </div>
      )}

      {!assignment && isOver && <div className="drop-indicator">Drop here</div>}
    </div>
  );
}
